import { debounce } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags/legacy-flags';
import { trackFieldSelected } from '@/lib/analytics/scribe/ui';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  ErrorSeverity,
  ErrorType,
  type OnboardingErrorStep,
} from '@/models/analytics/features/errors';
import { sanitizeErrorMessage } from '@/utils/analytics/trackErrorViewed';
import { FieldType, ScreenName } from '@heidi/runtime/analytics-vocabulary';

const TEXT_FIELD_DEBOUNCE_MS = 500;

/**
 * Lightweight wrapper around `trackFieldSelected` for the onboarding-v2
 * personalization steps. Centralises:
 *  - the `screen_name` (always `signup_full_page_view` while a user is in the
 *    personalization stack — this is the route they're on)
 *  - debounce for text inputs (one event per pause, not per keystroke)
 *  - first-fire-only semantics for fields whose value can change repeatedly
 *    but where we only care that the user touched them once
 *
 * Callers pass `screenSection` so we can disambiguate which step the field
 * lives on (e.g. `about_you`, `clinic`, `mic_test`) without inventing a new
 * `ScreenName` per step.
 */
export function useOnboardingFieldTracking(screenSection: string) {
  const trackImmediate = useCallback(
    (fieldName: string, fieldType: FieldType, fieldValue?: string) => {
      trackFieldSelected({
        fieldName,
        fieldType,
        screenName: ScreenName.SignupFullPageView,
        screenSection,
        ...(fieldValue && { fieldValue }),
      });
    },
    [screenSection]
  );

  /** First-fire-only — useful for inputs where the user might toggle a value
   *  several times but we only want one engagement event. */
  const firedFieldsRef = useRef<Set<string>>(new Set());
  const trackOnce = useCallback(
    (fieldName: string, fieldType: FieldType) => {
      if (firedFieldsRef.current.has(fieldName)) return;
      firedFieldsRef.current.add(fieldName);
      trackImmediate(fieldName, fieldType);
    },
    [trackImmediate]
  );

  /** Debounced text-field tracker. Returns a callable per `fieldName`; the
   *  underlying debounce instance is cached so repeated keystrokes coalesce. */
  const debouncedTrackersRef = useRef<
    Map<string, ReturnType<typeof debounce<() => void>>>
  >(new Map());
  const trackTextDebounced = useCallback(
    (fieldName: string) => {
      let tracker = debouncedTrackersRef.current.get(fieldName);
      if (!tracker) {
        tracker = debounce(
          () => trackImmediate(fieldName, FieldType.Text),
          TEXT_FIELD_DEBOUNCE_MS
        );
        debouncedTrackersRef.current.set(fieldName, tracker);
      }
      tracker();
    },
    [trackImmediate]
  );

  // Cancel any pending debounced calls when the consumer unmounts so we
  // don't fire field-selected after a user has navigated away.
  useEffect(() => {
    const trackers = debouncedTrackersRef.current;
    return () => {
      trackers.forEach((tracker) => tracker.cancel());
    };
  }, []);

  /**
   * Fires `onboarding_error_viewed` with the current onboarding variant.
   * Use for visible (or otherwise user-impacting) errors inside a step —
   * permission denials, validation warnings, save failures, etc.
   *
   * `errorMessage` is sanitized (truncated to 200 chars, trimmed); pass a
   * stable phrase rather than the raw HTTP body to keep events queryable
   * and PII-free. Use `errorCode` for the specific failure mode.
   */
  const trackError = useCallback(
    (options: {
      step: OnboardingErrorStep | string;
      errorType?: ErrorType | string;
      errorCode?: string;
      errorMessage: string;
      severity?: ErrorSeverity;
      country?: string;
    }) => {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.OnboardingErrorViewed,
        props: {
          step: options.step,
          error_type: options.errorType ?? ErrorType.UNKNOWN,
          ...(options.errorCode && { error_code: options.errorCode }),
          error_message: sanitizeErrorMessage(options.errorMessage),
          severity: options.severity ?? ErrorSeverity.WARNING,
          ...(options.country && { country: options.country }),
          onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
        },
      });
    },
    []
  );

  return useMemo(
    () => ({ trackImmediate, trackOnce, trackTextDebounced, trackError }),
    [trackImmediate, trackOnce, trackTextDebounced, trackError]
  );
}

/**
 * Fires `onboarding_error_viewed` exactly once per mount the first time
 * `isVisible` flips true. Use for in-step warning impressions (e.g. the
 * country/phone mismatch banner) so we can count how often users see them
 * without spamming the event on every render.
 */
export function useTrackOnboardingErrorImpression(
  isVisible: boolean,
  options: {
    step: OnboardingErrorStep | string;
    errorType?: ErrorType | string;
    errorCode?: string;
    errorMessage: string;
    severity?: ErrorSeverity;
    country?: string;
  }
): void {
  const { trackError } = useOnboardingFieldTracking('');
  const hasFiredRef = useRef(false);
  useEffect(() => {
    if (!isVisible || hasFiredRef.current) return;
    hasFiredRef.current = true;
    trackError(options);
    // Intentionally narrow deps to `isVisible` + `trackError` so changing
    // `options` between renders doesn't re-fire after the first impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, trackError]);
}
